import AlertPopup from "components/alert-popup/AlertPopup";
import ClientsStaff from "components/sections-staff/ClientsStaff";
import DashboardStaff from "components/sections-staff/DashboardStaff";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import MissionsWorkers from "components/workers/MissionsWorkers";
import PersonalStaff from "components/sections-staff/PersonalStaff";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import { FormattedMessage } from "react-intl";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ROLE_CSE,
  ROLE_DUTLER,
  ROLE_FMD,
  ROLE_JOB,
  ROLE_NDF,
  ROLE_PLPM,
} from "access-roles/roles";
import { STAFF_FORM_DEFAULT_VALUES } from "constants/forms";
import { getCurrentManager, getStatusLabel } from "utils/workerHelper";
import { getFormattedDate } from "utils/functions";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useMissionContext } from "api/mission/MissionProvider";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useReasonLeaveContext } from "api/settings/reason-leave/ReasonLeaveProvider";
import { useRecruitmentMethodContext } from "api/settings/recruitment-method/RecruitmentMethodProviders";
import { useRightsContext } from "api/rights/RightsProvider";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import { useSyntecContext } from "api/settings/syntec/SyntecProvider";
import { useUserContext } from "api/user/UserProvider";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import { useWindowSize, useEntityProfilePicture } from "utils/customHooks";
import LoaderV2 from "core/loader/LoaderV2";
import { formatCreatedData } from "utils/staffHelper";
import {
  DATATRACK_MODELS,
  DOCUMENTS_BASE_URL_BY_ENTITY,
  FILTERS_FOR_NAMES,
  LATE_MISSION_TYPE,
  QUERY_STATUS,
  SECTIONS,
  STAFF_PROFILES,
} from "constants/constants";
import { getClientSlug } from "utils/toolsManagerHelper";
import { useProspectionContext } from "api/directory/prospection/ProspectionProvider";
import { useProfileContext } from "api/settings/profile/ProfileProvider";
import PageContext from "pages/PageContext";
import { useForm } from "react-hook-form";
import { useNationalitiesContext } from "api/settings/nationalities/NationalitiesProvider";
import { useContractualStatusContext } from "api/settings/contractual-status/ContractualStatusProvider";
import { useSectorContext } from "api/settings/sector/SectorProvider";
import { useDashboardMissionLateContext } from "api/dashboard/mission/DashboardMissionLateProvider";
import ValidationModal from "components/modal/ValidationModal";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import { useDocumentsContext } from "api/documents/DocumentsProvider";
import { getISODate, statusHandler } from "utils/helper";
import {
  commonOnCloseHandler,
  validationModalTitleHelper,
} from "utils/modalsHelper";

const DisplayStaff = () => {
  const navigate = useNavigate();
  const {
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: STAFF_FORM_DEFAULT_VALUES,
  });
  const { currencyListGetter, currencyList } = useCurrencyContext();
  const { getAllRoles, roleList } = useRightsContext();
  const { profileList, profileListGetter } = useProfileContext();
  const {
    getTimeline,
    resetTimeline,
    timelineMissionsList,
    timelineQueryStatus,
    timelineEntityId,
  } = useMissionContext();
  const { createdFile, setCreatedFile, createFile } = useFileContext();
  const { recruitmentMethodList, recruitmentMethodListGetter } =
    useRecruitmentMethodContext();
  const { jobList, jobListGetter } = useJobContext();
  const { personsList, getPersonsByIds } = usePersonContext();
  const {
    missionCreatedLateList,
    missionClosedLateList,
    missionClosedInTimeList,
    getMissionLateList,
  } = useDashboardMissionLateContext();
  const {
    syntecCoefListGetter,
    syntecCoefStaffListGetter,
    syntecCoefStaffList,
  } = useSyntecContext();
  const { searchUsers, usersByIds, getNamesByFilters, namesByFilters } =
    useGeneralContext();
  const { fetchBranches, branches } = useBranchesContext();
  const { reasonLeaveListGetter, reasonLeaveList } = useReasonLeaveContext();
  const {
    getStaffBySlug,
    staffBySlug,
    staffList,
    staffListGetter,
    updateStaff,
    getStaffByArrayId,
    staffByIds,
    editStaffStatus,
    deleteStaffStatus,
    deleteStaff,
    displayStaffStatus,
    apiErrorResponse,
    setEditStaffStatus,
    setDeleteStaffStatus,
    staffFourYearsReview,
    getStaffFourYearsReview,
  } = useStaffContext();
  const { nationalitiesList, nationalitiesListGetter } =
    useNationalitiesContext();

  const { picture: entityPicture } = useEntityProfilePicture(
    staffBySlug?.picture_file_id
  );

  // Use createdFile as fallback when entity picture is not available
  const picture = createdFile?.deleted
    ? null
    : entityPicture || createdFile?.uri || createdFile?.picture_file_id;

  const {
    userDatatrack,
    userDatatrackGetter,
    userDatatrackStatus,
    filteredCorporateLife,
    getCorporateLifeByFilter,
  } = useUserDatatrackContext();
  const { lang, userScope, connectedUser, setApiErrorResponse } =
    useUserContext();
  const {
    getClientsByIds,
    clientsByIds,
    getAssignedClientsByUserId,
    assignedClients,
    patchClientTrafficLight,
    releaseClient,
  } = useClientContext();
  const {
    userProspectionByUserIdGetter,
    prospectionList,
    updateProspectionSector,
    updateProspectionTrafficLight,
  } = useProspectionContext();
  const { contractualStatusList, contractualStatusListGetter } =
    useContractualStatusContext();
  const {
    getAvailableDocuments,
    availableDocuments,
    getDocument,
    getDocumentStatus,
    setGetDocumentStatus,
    downloadReviewFile,
  } = useDocumentsContext();
  const { sectorList, sectorListGetter } = useSectorContext();
  const { slugSearched, setSlugSearched } = useContext(PageContext);
  const [isEnabled, setIsEnabled] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [section, setSection] = useState(SECTIONS.CLIENTS);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const size = useWindowSize();
  const { slug } = useParams();
  const staffSlug = slugSearched || slug;
  const isCurrentEntity = staffBySlug?.slug === staffSlug;
  const [refresh, setRefresh] = useState(false);
  const nationality = watch("nationality", []);
  const [coefPositionOptions, setCoefPositionOptions] = useState([]);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openModalIntent, setOpenModalIntent] = useState(null);
  const BASE_URL = DOCUMENTS_BASE_URL_BY_ENTITY.STAFF;

  const dateToISO = getISODate();
  const dateFromISO = getISODate(12);

  const [isCSE, setIsCSE] = useState(false);
  const [isPracticeLeader, setIsPracticeLeader] = useState(false);

  const currentQueryStatus = statusHandler({
    statusList: [editStaffStatus, deleteStaffStatus],
  });

  const validationModalTitle = validationModalTitleHelper({
    isEditAction: editStaffStatus !== QUERY_STATUS.INITIAL,
    isDeleteAction: deleteStaffStatus !== QUERY_STATUS.INITIAL,
  });

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    sectorListGetter();
  }, []);

  useEffect(() => {
    const ids = staffByIds?.map((elem) => elem?.staff?.id);
    if (ids?.length > 0) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id: ids,
      });
    }
  }, [staffByIds]);

  useEffect(() => {
    nationalitiesListGetter();
  }, [lang]);

  useEffect(() => {
    staffListGetter("mgr");
  }, [staffListGetter, userScope]);

  useEffect(() => {
    setReadOnly(true);
    setCreatedFile({});
    !_.isNil(userScope) && getStaffBySlug(slugSearched || slug);
    setRefresh(false);
  }, [slug, slugSearched, userScope, refresh]);

  useEffect(() => {
    if (displayStaffStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [displayStaffStatus, userScope, navigate]);

  useEffect(() => {
    const { id, gp_id, manager_id, referer_internal_id, role } =
      staffBySlug || {};

    setIsCSE(role?.includes(ROLE_CSE));
    setIsPracticeLeader(role?.includes(ROLE_PLPM));

    if (staffBySlug && !_.isNil(userScope)) {
      const ids = [gp_id, manager_id, referer_internal_id].filter(Boolean);
      if (ids?.length > 0) {
        getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          id: ids,
        });
      }
    }

    setCreatedFile({});
    setSlugSearched("");
    resetTimeline();

    if (id) {
      getAssignedClientsByUserId(id);
      getAvailableDocuments({
        entityId: id,
        BASE_URL,
      });
    }
  }, [staffBySlug]);

  useEffect(() => {
    getAllRoles([ROLE_DUTLER, ROLE_NDF, ROLE_FMD, ROLE_JOB]);
    recruitmentMethodListGetter();
    fetchBranches();
    jobListGetter();
    reasonLeaveListGetter();
    syntecCoefListGetter();
    currencyListGetter();
    profileListGetter();
    contractualStatusListGetter();
    syntecCoefStaffListGetter();
    staffBySlug?.id &&
      userDatatrackGetter({
        id: staffBySlug?.id,
        model: DATATRACK_MODELS.MANAGER,
      });
    staffBySlug?.id &&
      userDatatrackGetter({
        id: staffBySlug?.id,
        model: DATATRACK_MODELS.POS_COEF,
      });
  }, []);

  const getOptions = (search) => {
    getNamesByFilters({
      profile: [...STAFF_PROFILES, "cslt", "str"],
      types: [FILTERS_FOR_NAMES.PERSON],
      search,
    });
  };

  const updateData = useCallback(
    async (info) => {
      const formattedData = formatCreatedData({ ...info, enabled: isEnabled });
      window.scrollTo(0, 0);
      // Awaited so the caller's submit lock covers the whole request
      await updateStaff(staffBySlug?.id, formattedData);
    },
    [isEnabled, staffBySlug, updateStaff, userScope]
  );

  const onSubmit = async (info) => {
    await updateData({
      ...info,
      picture_file_id: createdFile?.deleted
        ? null
        : createdFile?.picture_file_id ||
          createdFile?.uri ||
          staffBySlug?.picture_file_id,
    });
  };

  useEffect(() => {
    if (staffBySlug && !_.isNil(userScope)) {
      setIsEnabled(staffBySlug.enabled);
    }
  }, [staffBySlug, userScope]);

  const initHeader = () => {
    setIsEnabled(staffBySlug.enabled);
  };

  useEffect(() => {
    if (timelineMissionsList.length > 0) {
      const workersIds = timelineMissionsList
        .map((elm) => elm.worker_id)
        .join(",");
      const clientsIds = timelineMissionsList
        .map((elm) => elm.client_id)
        .join(",");
      getPersonsByIds({ ids: workersIds });
      getClientsByIds({ ids: clientsIds });
    }
  }, [timelineMissionsList, staffBySlug, userScope]);

  useEffect(() => {
    if (
      section === SECTIONS.DASHBOARD &&
      (missionCreatedLateList?.length > 0 ||
        missionClosedLateList?.length > 0 ||
        missionClosedInTimeList?.length > 0)
    ) {
      const workerIds = _.chain(missionCreatedLateList)
        .map("worker_id")
        .concat(_.map(missionClosedLateList, "worker_id"))
        .concat(_.map(missionClosedInTimeList, "worker_id"))
        .uniqWith(_.isEqual)
        .join(",")
        .value();
      getPersonsByIds({ ids: workerIds });
    }
  }, [section, userScope]);

  useEffect(() => {
    if (section !== SECTIONS.DASHBOARD || !staffBySlug?.id) return;
    getStaffFourYearsReview({ id: staffBySlug.id });
  }, [section, staffBySlug?.id, getStaffFourYearsReview]);

  useEffect(() => {
    switch (section) {
      case SECTIONS.PERSONAL:
        !_.isNil(userScope) && getStaffBySlug(slug);
        break;
      case SECTIONS.DASHBOARD:
        if (staffBySlug?.id) {
          Object.entries(LATE_MISSION_TYPE)?.forEach(([key, value]) =>
            getMissionLateList({
              manager_id: staffBySlug.id,
              type: value,
            })
          );
          getCorporateLifeByFilter({
            personId: staffBySlug.id,
            dateFrom: dateFromISO,
            dateTo: dateToISO,
          });
        }
        break;
      case SECTIONS.MISSIONS:
        getTimeline({
          filters: { type: "staff", id: staffBySlug?.id, missionsOnly: true },
        });
        break;
      case SECTIONS.CLIENTS:
        staffBySlug?.id &&
          userProspectionByUserIdGetter({
            id: staffBySlug?.id,
            scope: userScope,
          });
        staffBySlug?.id && getAssignedClientsByUserId(staffBySlug?.id);
        break;
      default:
        break;
    }
  }, [section, refresh]);

  const getFormattedMission = (list) => {
    return list.map((elm) => ({
      name: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {elm?.mission_name}
          <br />
          {elm?.mission_analytical_code}
        </div>
      ),
      client_id: (
        <Link
          to={`/${userScope}/client/${
            elm?.client_id && getClientSlug(elm?.client_id, clientsByIds)
          }`}
        >
          {elm?.client_name}
        </Link>
      ),
      consultant: (
        <div>
          <Link
            to={`/${userScope}/${
              elm?.worker_profile === "cslt" ? "consultant" : "subcontractor"
            }/${elm?.worker_id && elm?.worker_slug}`}
          >
            {elm?.worker_name}
          </Link>
          {elm?.worker_profile === "str" && <div>(STR)</div>}
        </div>
      ),
      start_date: getFormattedDate(elm?.start_date, lang),
      end_date: (
        <div style={{ textAlign: "center" }}>
          {elm?.end_date === null ? "-" : getFormattedDate(elm?.end_date, lang)}
        </div>
      ),
      status: (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {getStatusLabel(elm?.mission_status)}
          <Link to={`/${userScope}/missions/${elm?.mission_slug}`}>
            <FormattedMessage
              id="view_sheet"
              defaultMessage={"Voir la fiche"}
            />
          </Link>
        </div>
      ),
      name_for_search: [
        elm?.mission_name,
        elm?.client_name,
        elm?.mission_analytical_code,
        elm?.worker_analytical_code,
      ]
        .filter(Boolean)
        .join(" "),
    }));
  };

  useEffect(() => {
    currentQueryStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
    if (editStaffStatus === QUERY_STATUS.SUCCESS) {
    }
  }, [currentQueryStatus]);

  useEffect(() => {
    const coefPositionOptions = syntecCoefStaffList.flatMap((e) =>
      e.coef_position_syntec.map((elm) => ({
        ...elm,
        contractual_status_id: elm.contractual_status_id,
        value: `${elm.position}${
          elm.coefficient > 0 ? " " + elm.coefficient : ""
        }`,
        id: elm.id,
      }))
    );
    setCoefPositionOptions(coefPositionOptions);
  }, [syntecCoefStaffList]);

  const value = useMemo(
    () => ({
      editStaffStatus,
      updateProspectionSector,
      updateProspectionTrafficLight,
      sectorList,
      cellHighlight: cellHighlight,
      setIsValidated: setIsValidated,
      isValidated: isValidated,
      nationality,
      userDatatrack,
      branches: branches,
      connectedUser: connectedUser,
      currencyList: currencyList,
      davFunctions: jobList,
      deleted: staffBySlug?.email?.includes("DELETED"),
      getOptions: getOptions,
      getStaffByArrayId: getStaffByArrayId,
      id: staffBySlug?.id,
      email: staffBySlug?.email,
      isEnabled: isEnabled,
      isMobile: size.width < 600,
      lang: lang,
      leavingReasons: reasonLeaveList,
      managersList: staffList,
      onDelete: deleteStaff,
      personsByProfileList: namesByFilters,
      personsList: personsList,
      readOnly: readOnly,
      recruitmentMethodList: recruitmentMethodList,
      searchUsers: searchUsers,
      section: section,
      setIsEnabled: setIsEnabled,
      setReadOnly: setReadOnly,
      setSection: setSection,
      staffByIds: staffByIds,
      syntecCoef: coefPositionOptions,
      userBySlug: staffBySlug,
      userManager: getCurrentManager(userDatatrack?.manager),
      userScope: userScope,
      usersByIds: usersByIds,
      userById: staffBySlug,
      profileList: profileList,
      refresh,
      setRefresh: setRefresh,
      nationalities: nationalitiesList,
      contractualStatusList: contractualStatusList,
      createFile,
      createdFile,
      setCreatedFile,
      setCode,
      setStatus,
      picture,
      roleList,
      patchClientTrafficLight,
      releaseClient,
      missionCreatedLateList,
      missionClosedLateList,
      missionClosedInTimeList,
      managersNames: namesByFilters,
      filteredCorporateLife,
      entity: ENTITY.STAFF,
      entityStatus: userDatatrackStatus,
      entitySlug: staffBySlug?.slug,
      getStaffBySlug,
      apiErrorResponse,
      availableDocuments,
      getDocument,
      getDocumentStatus,
      assignedClients,
      isValidationModalOpen: isOpen,
      isCSE,
      setIsCSE,
      isPracticeLeader,
      setIsPracticeLeader,
      slug,
      openModalIntent,
      setOpenModalIntent,
      staffFourYearsReview,
      downloadReviewFile,
    }),
    [
      editStaffStatus,
      sectorList,
      namesByFilters,
      cellHighlight,
      isValidated,
      nationality,
      refresh,
      branches,
      connectedUser,
      currencyList,
      isEnabled,
      jobList,
      lang,
      personsList,
      readOnly,
      reasonLeaveList,
      recruitmentMethodList,
      section,
      staffByIds,
      staffList,
      userScope,
      usersByIds,
      staffBySlug,
      profileList,
      nationalitiesList,
      contractualStatusList,
      coefPositionOptions,
      createFile,
      createdFile,
      setCreatedFile,
      releaseClient,
      roleList,
      missionCreatedLateList,
      missionClosedLateList,
      missionClosedInTimeList,
      namesByFilters,
      filteredCorporateLife,
      getStaffBySlug,
      apiErrorResponse,
      availableDocuments,
      getDocument,
      assignedClients,
      userDatatrackStatus,
      isOpen,
      isCSE,
      setIsCSE,
      isPracticeLeader,
      setIsPracticeLeader,
      slug,
      openModalIntent,
      staffFourYearsReview,
      downloadReviewFile,
    ]
  );

  return (
    <InfoContext.Provider value={value}>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <DocumentDownloadModal
        status={getDocumentStatus}
        onClose={() => setGetDocumentStatus(QUERY_STATUS.INITIAL)}
      />
      <ValidationModal
        messages={{
          pendingTitleId: validationModalTitle,
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: validationModalTitle,
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: validationModalTitle,
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
        open={isOpen}
        status={currentQueryStatus}
        onClose={() =>
          commonOnCloseHandler({
            editEntityStatus: editStaffStatus,
            deleteEntityStatus: deleteStaffStatus,
            setDeleteEntityStatus: setDeleteStaffStatus,
            setEditEntityStatus: setEditStaffStatus,
            setIsOpen,
            setReadOnly,
            userScope,
            navigate,
          })
        }
      />
      {(!isCurrentEntity ||
        ![
          QUERY_STATUS.SUCCESS,
          QUERY_STATUS.ERROR,
          QUERY_STATUS.NOT_FOUND,
        ].includes(displayStaffStatus)) && <LoaderV2 loaderSize="fullpage" />}
      {isCurrentEntity && displayStaffStatus === QUERY_STATUS.SUCCESS && (
        <>
          <InfoHeaderAction />
          {section === SECTIONS.CLIENTS && (
            <ClientsStaff
              readOnly={readOnly}
              userClientList={assignedClients}
              lang={lang}
              userScope={userScope}
              prospectionList={prospectionList}
              staffBySlug={staffBySlug}
            />
          )}
          {section === SECTIONS.DASHBOARD && (
            <DashboardStaff workers={personsList} />
          )}
          {section === SECTIONS.PERSONAL && (
            <PersonalStaff
              readOnly={readOnly}
              setReadOnly={setReadOnly}
              onSubmit={onSubmit}
              initHeader={initHeader}
              getStatus={setStatus}
              getCode={setCode}
              status={status}
              code={code}
            />
          )}

          {section === SECTIONS.MISSIONS &&
            (timelineEntityId !== staffBySlug?.id ||
              timelineQueryStatus === QUERY_STATUS.LOADING) && (
              <LoaderV2 loaderSize="fullpage" />
            )}
          {section === SECTIONS.MISSIONS &&
            timelineEntityId === staffBySlug?.id &&
            timelineQueryStatus === QUERY_STATUS.SUCCESS && (
              <MissionsWorkers
                formattedMissions={getFormattedMission(timelineMissionsList)}
                role={ENTITY.STAFF}
                workers={personsList}
                clients={clientsByIds}
                id="missionsStaff"
              />
            )}
        </>
      )}
    </InfoContext.Provider>
  );
};

export default DisplayStaff;
